export function statusBadgeProps(state: string, isOnline: boolean): { variant: "neutral" | "success" | "error" | "warning" | "info" | "primary"; outline?: boolean } {
  if (!isOnline) return { variant: "neutral" };
  const s = (state || "").toLowerCase();
  if (s === "charging" || s === "charging_simple") return { variant: "success" };
  if (s === "error") return { variant: "error" };
  if (s === "ready") return { variant: "warning", outline: true };
  if (s === "connected") return { variant: "info", outline: true };
  if (s === "standby") return { variant: "primary", outline: true };
  if (s.startsWith("unsupported")) return { variant: "warning" };
  if (s === "blocked") return { variant: "neutral" };
  return { variant: "neutral", outline: true };
}

/**
 * Normalizes a raw telemetry `state_name` to the status value understood by the
 * `operator.units.status_option` select (the same vocabulary the status filter
 * dropdown uses), so a status badge can show a localized label instead of the
 * raw server word. Values the select does not name fall through to its `other`
 * branch, which renders the raw text. `charging_simple` is shown as charging and
 * any `unsupported_v*` protocol marker as unsupported, matching statusBadgeProps.
 */
export function statusFilterValue(state: string): string {
  const s = (state || "").toLowerCase();
  if (s === "charging_simple") return "charging";
  if (s.startsWith("unsupported")) return "unsupported";
  return s;
}

type TranslateFn = (key: string, params?: Readonly<Record<string, string | number>>) => string;

/**
 * The localized label for a unit's status badge, shared by the device table and
 * the card grid so both read the same way. Offline units and an empty/unknown
 * state keep their own labels; a live online state is translated through the
 * `operator.units.status_option` catalog key — the same vocabulary as the status
 * filter dropdown — instead of being shown as the raw server word.
 */
export function statusBadgeLabel(t: TranslateFn, state: string, isOnline: boolean): string {
  if (!isOnline) return t("operator.units.badge_offline");
  const raw = state || "";
  if (!raw || raw.toLowerCase() === "unknown") return t("operator.units.badge_unknown");
  return t("operator.units.status_option", { status: statusFilterValue(raw), raw });
}

export function kwhFromSessionEnergy(sessionEnergy: number): string {
  return (sessionEnergy / 1000).toFixed(2);
}

export function formatNum(n: number | undefined | null, digits = 0): string {
  const v = typeof n === "number" && isFinite(n) ? n : 0;
  return v.toFixed(digits);
}
